import { useEffect, useRef, useState, useCallback } from "react";
import { ShieldCheck, ShieldAlert, LogIn } from "lucide-react";
import { cn } from "../../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL;
const PING_INTERVAL_MS = 120000; // 2 minutes

/**
 * Tiny top-bar indicator that pings /api/auth/me every 2 minutes.
 * - OK   → muted check shield (optionally hidden on healthy state)
 * - STALE → amber banner + "Re-login" button (clears storage, redirects)
 *
 * Prevents the silent "why can't I save?" class of bug caused by stale JWTs
 * after an admin forces a logout or a token hits its max lifetime.
 */
export const SessionHealthIndicator = ({ dark = false }) => {
  const [status, setStatus] = useState("ok"); // ok | stale
  const cancelledRef = useRef(false);

  const ping = useCallback(async () => {
    // Session lives in an httpOnly cookie (DEF-24); the JWT is no longer kept
    // in localStorage. Send the cookie with `credentials: "include"` so this
    // reflects the REAL session — otherwise it would always read "stale" and
    // flash a false "Session expired" pill for every logged-in user.
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (cancelledRef.current) return;
      if (res.status === 401 || res.status === 403) setStatus("stale");
      else if (res.ok) setStatus("ok");
    } catch {
      // Network errors shouldn't flip the indicator — user is offline, not logged out
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    // First ping happens 5 seconds after mount (lets initial page loads settle)
    const first = setTimeout(ping, 5000);
    const interval = setInterval(ping, PING_INTERVAL_MS);
    return () => {
      cancelledRef.current = true;
      clearTimeout(first);
      clearInterval(interval);
    };
  }, [ping]);

  const reLogin = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  if (status === "ok") {
    return (
      <div
        className={cn(
          "flex items-center gap-1 text-[10px]",
          dark ? "text-emerald-400/70" : "text-emerald-600/70"
        )}
        title="Session active"
        data-testid="session-health-ok"
      >
        <ShieldCheck className="w-3 h-3" />
      </div>
    );
  }

  // Stale — show an amber pill with a one-click re-login CTA
  return (
    <button
      onClick={reLogin}
      className={cn(
        "flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-semibold",
        "bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200",
        "transition-colors animate-pulse"
      )}
      title="Your session has expired. Click to re-login."
      data-testid="session-health-stale"
    >
      <ShieldAlert className="w-3 h-3" />
      <span className="hidden sm:inline">Session expired</span>
      <LogIn className="w-3 h-3" />
    </button>
  );
};
